import { Alert, Box, useTheme } from "@mui/material";

import { useAuth } from "@/providers";
import { DragAndDropFiles } from "@/shared/components/DragAndDropFiles";
import { type FileRecord } from "@/shared/domain/files";
import { SortColumn, SortDirection } from "@/shared/domain/files/FileService";
import { FileSystemNode } from "@/shared/domain/files/filesystem";

import { DatasetDisplays } from "./DatasetDisplays";
import { PaginationConfig } from "./state";

interface DragAndDropDisplayProps {
  currentPage: FileSystemNode[] | undefined;
  datasetId: string;
  displayMode: string;
  error: Error | null;
  fileInputRef: React.RefObject<HTMLInputElement>;
  filePrefix: string | undefined;
  gridItems: number;
  handleDeleteRow: (row: FileSystemNode) => void;
  handleRowDoubleClick: (row: FileSystemNode) => void;
  handleSelectRows: (rows: FileSystemNode[], isSelected: boolean) => void;
  isFetching: boolean;
  isPending: boolean;
  isPlaceholderData: boolean;
  isSearching: boolean;
  onClearError: () => void;
  onChangeInput: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  onDropComplete: (error?: Error) => Promise<void>;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => void;
  onRowsPerPageChange: (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | undefined,
  ) => void;
  selectedItems: FileSystemNode[];
  sortColumn: SortColumn;
  sortDirection: SortDirection;
  onSort: (column: SortColumn, direction: SortDirection) => void;
  tableConfig: PaginationConfig;
  totalItemCount: number | undefined;
  handleActionConfirmation: (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => void;
  /**
   * Opens the ingest wizard for a robot description. Owned by the browser.
   *
   * Optional — absent when `release-3d-assets` is off, and callers pass it
   * straight through, so the components that act on it handle its absence.
   */
  onIngestAsAsset?: (file: FileRecord) => void;
}

export const DragAndDropDisplay: React.FC<DragAndDropDisplayProps> = ({
  currentPage,
  datasetId,
  displayMode,
  error,
  fileInputRef,
  filePrefix,
  gridItems,
  handleDeleteRow,
  handleRowDoubleClick,
  handleSelectRows,
  isFetching,
  isPending,
  isPlaceholderData,
  isSearching,
  onClearError,
  onChangeInput,
  onDropComplete,
  onPageChange,
  onRowsPerPageChange,
  selectedItems,
  sortColumn,
  sortDirection,
  onSort,
  tableConfig,
  totalItemCount,
  handleActionConfirmation,
  onIngestAsAsset,
}) => {
  const theme = useTheme();
  const { currentOrganization } = useAuth();

  let dragAndDropContent;

  if (error) {
    dragAndDropContent = (
      <Box sx={{ mt: theme.spacing(2) }}>
        <Alert onClose={onClearError} severity="error">
          {error.message}
        </Alert>
      </Box>
    );
  } else {
    dragAndDropContent = (
      <DragAndDropFiles
        containerStyle={{
          marginTop: theme.spacing(1),
          backgroundColor: "transparent",
        }}
        displayOverlayOnFileHover={true}
        orgId={currentOrganization?.org_id ?? ""}
        datasetId={datasetId}
        onDropComplete={onDropComplete}
        filePrefix={filePrefix}
      >
        <DatasetDisplays
          currentPage={currentPage}
          displayMode={displayMode}
          datasetId={datasetId}
          gridItems={gridItems}
          handleDeleteRow={handleDeleteRow}
          handleRowDoubleClick={handleRowDoubleClick}
          isFetching={isFetching}
          isPending={isPending}
          isPlaceholderData={isPlaceholderData}
          isSearching={isSearching}
          onPageChange={(event, newPage) => {
            onPageChange(event, newPage);
          }}
          onRowsPerPageChange={(event) => {
            onRowsPerPageChange(event);
          }}
          selectedItems={selectedItems}
          handleSelectRows={handleSelectRows}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={onSort}
          tableConfig={tableConfig}
          totalItemCount={totalItemCount}
          handleActionConfirmation={handleActionConfirmation}
          onIngestAsAsset={onIngestAsAsset}
        />

        <input
          type="file"
          multiple
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={(e) => {
            void onChangeInput(e);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }}
        />
      </DragAndDropFiles>
    );
  }

  return dragAndDropContent;
};
